import { getCleanedUrlParams, request } from '@utils';
import { LIMIT } from '@constants';
import { USER_HISTORY_CHANGES_DEFAULT_PAGE_SIZE } from 'hooks/users/constants';

const filterUsers = async ({ params = {}, payload = {} } = {}) => {
  const body = {
    body: payload,
  };
  const results = await request.post(
    `/v1/users/filter?${new URLSearchParams(params)}`,
    body
  );

  if (results?.error) {
    return Promise.reject(results?.message);
  }

  return results;
};

const getUsers = async (payload) => {
  if (!payload?.pageSize) {
    payload.pageSize = LIMIT;
  }
  const results = await request.get(`/v1/users`, payload);

  if (results?.error) {
    return Promise.reject(results?.message);
  }

  return results;
};

const getUsersEnums = async () => {
  const results = await request.get(`/v1/users/enums`);

  if (results?.error) {
    return Promise.reject(results?.message);
  }

  return results;
};

const getUser = async (id) => {
  const results = await request.get(`/v1/users/${id}`);

  if (results?.error) {
    return Promise.reject(results?.message);
  }

  return results;
};

const getUserWithSubs = async (id) => {
  const results = await request.get(`/v1/users/${id}/with-subs`);

  if (results?.error) {
    return Promise.reject(results?.message);
  }

  return results;
};

const getUserBookings = async (id, params = {}) => {
  const results = await request.get(
    `/v1/users/${id}/bookings?${new URLSearchParams(params)}`
  );

  if (results?.error) {
    return Promise.reject(results?.message);
  }

  return results;
};

const getUserSubscriptions = async (id, params = {}) => {
  const results = await request.get(
    `/v1/users/${id}/subscriptions?${new URLSearchParams(params)}`
  );

  if (results?.error) {
    return Promise.reject(results?.message);
  }

  return results;
};

const getUserDocuments = async (id) => {
  const results = await request.get(`/v1/users/${id}/docs`);

  if (results?.error) {
    return Promise.reject(results?.message);
  }

  return results;
};

const createUser = async (params) => {
  const body = {
    body: params,
  };
  const results = await request.post(`/v1/users`, body);

  if (results?.error) {
    return Promise.reject(results?.message);
  }

  return results;
};

const uploadUserDocument = async (userId, document, documentType) => {
  const fd = new FormData();
  fd.append('documents', document);
  fd.append('type', documentType);

  const body = {
    body: fd,
    header: {
      Accept: '*/*',
    },
  };
  const results = await request.post(`/v1/users/${userId}/docs`, body);

  if (results?.error) {
    return Promise.reject(results?.message);
  }

  return results;
};

const deleteUserDocument = async (userId, documentId) => {
  const results = await request.remove(
    `/v1/users/${userId}/docs/${documentId}`
  );

  if (results?.error) {
    return Promise.reject(results?.message);
  }

  return results;
};

const partialUpdateUser = async (id, params) => {
  const body = {
    body: params,
  };
  const results = await request.patch(`/v1/users/${id}`, body);

  if (results?.error) {
    return Promise.reject(results?.message);
  }

  return results;
};

const updateUser = async (id, params) => {
  const body = {
    body: params,
  };
  const results = await request.put(`/v1/users/${id}`, body);

  if (results?.error) {
    return Promise.reject(results?.message);
  }

  return results;
};

const getUserByEmail = async (email) => {
  const results = await request.get(`/v1/users/email/${email}`);

  if (results?.error) {
    return Promise.reject(results?.message);
  }

  return results;
};

const getUserCreditScore = async (id) => {
  const results = await request.get(`/v1/users/${id}/creditscore`);

  if (results?.error) {
    return Promise.reject(results?.message);
  }

  return results;
};

const updateUserCreditScore = async (id, params) => {
  const body = {
    body: params,
  };
  const results = await request.post(`/v1/users/${id}/creditscore`, body);

  if (results?.error) {
    return Promise.reject(results?.message);
  }

  return results;
};

const getUserOrders = async (id, payload = {}) => {
  if (!payload?.page) {
    payload.page = 1;
  }
  if (!payload?.pageSize) {
    payload.pageSize = LIMIT;
  }

  const results = await request.get(`/v1/users/${id}/orders`, payload);

  if (results?.error) {
    return Promise.reject(results?.message);
  }

  return results;
};

const addUserBillingInfo = async (subscriberId, payload) => {
  const params = {
    body: payload,
  };
  const results = await request.post(
    `/v1/users/${subscriberId}/billingInfo`,
    params
  );

  if (results?.error) {
    return Promise.reject(results?.message);
  }

  return results;
};

const editUserBillingInfo = async (userId, payload) => {
  const params = {
    body: payload,
  };
  const results = await request.patch(`/v1/users/${userId}`, params);

  if (results?.error) {
    return Promise.reject(results?.message);
  }

  return results;
};

const addUserSubscription = async (id, payload) => {
  const params = {
    body: payload,
  };

  const results = await request.post(`/v1/users/${id}/subscriptions`, params);

  if (results?.error) {
    return Promise.reject(results?.message);
  }

  return results;
};

const getUserBillingInfoByTin = async ({ userId, tin }) => {
  const results = await request.get(`/v1/users/${userId}/billingInfo/${tin}`);

  if (results?.error) {
    return Promise.reject(results?.message);
  }

  return results;
};

const getUserHistoryChanges = async ({
  userId,
  page = 1,
  pageSize = USER_HISTORY_CHANGES_DEFAULT_PAGE_SIZE,
  order = 'desc',
  orderBy = 'createdAt',
}) => {
  const params = {
    page,
    pageSize,
    order,
    orderBy,
  };

  const cleanParams = getCleanedUrlParams(params);

  const results = await request.get(
    `/v1/users/${userId}/history?${new URLSearchParams(cleanParams)}`
  );

  if (results?.error) {
    return Promise.reject(results?.message);
  }

  return results;
};

const getUserSMS = async ({ params, payload }) => {
  const body = {
    body: payload,
  };
  const results = await request.post(
    `/v1/communications/filter?${new URLSearchParams(params)}`,
    body
  );

  if (results?.error) {
    return Promise.reject(results?.message);
  }

  return results;
};

const getUserComments = async (entityId) => {
  const results = await request.get(`/v1/comments?entityID=${entityId}`);

  if (results?.error) {
    return Promise.reject(results?.message);
  }

  return results || [];
};

const addUserComment = async (payload) => {
  const params = {
    body: payload,
  };
  const results = await request.post(`/v1/comments`, params);

  if (results?.error) {
    return Promise.reject(results?.message);
  }

  return results;
};

const deleteUserComment = async (commentId) => {
  const results = await request.remove(`/v1/comments/${commentId}`);

  if (results?.error) {
    return Promise.reject(results?.message);
  }

  return results;
};

export {
  filterUsers,
  getUsers,
  getUser,
  getUserBookings,
  getUserSubscriptions,
  getUserDocuments,
  createUser,
  getUsersEnums,
  uploadUserDocument,
  deleteUserDocument,
  updateUser,
  getUserByEmail,
  getUserCreditScore,
  getUserOrders,
  updateUserCreditScore,
  addUserBillingInfo,
  editUserBillingInfo,
  getUserWithSubs,
  addUserSubscription,
  getUserBillingInfoByTin,
  partialUpdateUser,
  getUserHistoryChanges,
  getUserSMS,
  getUserComments,
  addUserComment,
  deleteUserComment,
};
